import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Heading } from "../../ui/heading";
import { Button } from "../../ui/button";
import { Separator } from "../../ui/separator";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Input } from "../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import Sidebar from "../../DashboardNavigation/sidebar";
import Header from "../../DashboardNavigation/header";
import BreadCrumb from "../../breadcrumb";
import {
  UserSignUpFormValues,
  userSignUpSchema,
  UserUpdateFormValues,
  userUpdateSchema,
} from "./user-zod-schema";
import { handleSignUp } from "../../../API/Users/authService";
import {
  getAllRoles,
  getUserById,
  updateUser,
} from "../../../API/Roles/roleService";

export const UserForm: React.ComponentType<any> = () => {
  const [userData, setUserData] = useState<any>();
  const { id } = useParams();
  const initialData = id ? parseInt(id) : undefined;
  const router = useNavigate();

  const [loading, setLoading] = useState(false);
  const title = initialData
    ? "Modifier utilisateur"
    : "Ajouter un nouveau utilisateur";
  const description = initialData
    ? "Modifier utilisateur"
    : "Créer nouveau utilisateur";
  const action = initialData ? "Mise à jour" : "Ajouter";

  const form = useForm<UserSignUpFormValues | UserUpdateFormValues>({
    resolver: zodResolver(initialData ? userUpdateSchema : userSignUpSchema),
  });

  useEffect(() => {
    const fetchData = async () => {
      if (initialData) {
        const userData = await getUserById(initialData);
        setUserData(userData);
      }
    };

    if (initialData) {
      fetchData();
    }
  }, [initialData]);

  const [role, setRole] = useState<any[]>([]);
  const fetchData = async () => {
    const response = await getAllRoles();
    setRole(response?.roles);
  };
  useEffect(() => {
    fetchData();
  }, []);

  const onSubmit = async () => {
    setLoading(true);
    const formData = form.getValues();
    try {
      if (initialData) {
        await updateUser(initialData, formData, router);
      } else {
        await handleSignUp(formData, form.setValue, userSignUpSchema, router);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { title: "Utilisateurs", link: "/admin-dashboard/utilisateurs" },
    { title: "Ajouter", link: "/admin-dashboard/utilisateurs/ajouter" },
  ];

  return (
    <>
      <Header />
      <div className="flex h-screen overflow-hidden">
        <Sidebar />
        <div className="w-full p-10">
          <BreadCrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between">
            <Heading title={title} description={description} />
          </div>
          <Separator />
          <Form {...form}>
            <form
              className="w-full space-y-8"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div className="gap-8 md:grid md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom </FormLabel>
                      <FormControl>
                        <Input
                          disabled={loading}
                          placeholder={userData?.lastName || "Nom..."}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Prénom</FormLabel>
                      <FormControl>
                        <Input
                          disabled={loading}
                          placeholder={userData?.firstName || "Prénom..."}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          disabled={loading}
                          {...field}
                          placeholder={userData?.email || "example@mail.com"}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role</FormLabel>
                      <Select
                        disabled={loading}
                        onValueChange={field.onChange}
                        value={field.value}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              defaultValue={field.value}
                              placeholder="Selectionner role"
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {role?.map((role) => (
                            <SelectItem key={role.id} value={role.designation}>
                              {role.designation}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mot de passe </FormLabel>
                      <FormControl>
                        <Input
                          disabled={loading}
                          placeholder="Mot de passe..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex space-x-5">
                <div>
                  <Button
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto"
                    type="submit"
                  >
                    {action}
                  </Button>
                </div>
                <div>
                  <Button
                    onClick={() => router(-1)}
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto bg-red-500"
                    type="button"
                  >
                    {"Annuler"}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
};
